import React, { useState, useEffect } from "react";
import { getSpecialties, getInsurancePlans } from "../APIs/dropdownApi";
import {
  ETHNICITIES,
  LANGUAGES,
  GENDER,
} from "../DropdownOptions/LanguageEthnicityGender";
import {
  savePatientPreferences,
  formatPatientPreferencesSavePayload,
  getPatientPreference,
} from "../APIs/patientService";
import { useNavigate } from "react-router-dom";
import { getStates, getCitiesByState } from "../APIs/geoApi";
import usStates from "../data/us-states.json";
import { fetchCurrentUser } from "../APIs/authApi";
import CompletePatientProfileModal from "../Patient/CompletePatientProfileModal";
import keycloak from "../APIs/keycloak";
import { showToast } from "../APIs/utils/toastManager";
import { useBackendReady } from "../APIs/BackendReadyContext";
import Header from "../modals/Header.js";

export default function ProviderMatchQuestions({ patientId, switchTab }) {
  const backendReady = useBackendReady();
  const navigate = useNavigate();

  // Dropdown data from backend
  const [specialties, setSpecialties] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  //Handle signup/login modal
  // Auth modal gating (guest users)
  const [showAuthModal, setShowAuthModal] = useState(false);
  const closeAuthModal = () => setShowAuthModal(false);

  // Keycloak handlers (same as ProviderSearchPage)
  const handleSignIn = () => {
    if (!backendReady) {
      showToast("MedMaze is loading, please wait.", "info");
      return;
    }

    keycloak.login({
      redirectUri: window.location.origin + "/ProviderSearchPage",
    });
  };

  const handleSignUp = () => {
    keycloak.register();
  };

  const [stateOptions, setStateOptions] = useState([]); // [{value,label}]
  const [cityOptions, setCityOptions] = useState([]); // [{value,label}]
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [, setStateIndex] = useState({}); // {"3936": { code:"AZ", name:"Arizona" }, ...}

  // Gating
  const [currentUser, setCurrentUser] = useState(null);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const token = keycloak?.token;

  const [formData, setFormData] = useState({
    patientId,
    specialty: "",
    state: "",
    city: "",
    stateId: "",
    cityId: "",
    insuranceIds: [],
    languageNames: [], // ✅ initialized as array (not undefined)
    ethnicity: "",
    gender: "",
    minYearsOfExperience: "", // ✅ start with empty string (controlled)
    maxYearsOfExperience: "", // ✅ start with empty string
  });

  // 🔹 Fetch Specialties and Insurances
  useEffect(() => {
    getSpecialties().then((data) => setSpecialties(data || []));
    getInsurancePlans().then((data) => setInsurances(data || []));
    (async () => {
      setLoadingStates(true);
      try {
        const states = await getStates(); // [{id, name, stateCode}, ...]
        setStateOptions(states.map((s) => ({ value: s.id, label: s.name })));
        const index = Object.fromEntries(
          states.map((s) => [
            String(s.id),
            { code: s.stateCode, name: s.name },
          ]),
        );
        setStateIndex(index);
      } finally {
        setLoadingStates(false);
      }
    })();

    //Load current user for gating (BASIC_ACCOUNT vs COMPLETED_PROFILE)
    (async () => {
      try {
        const me = await fetchCurrentUser();
        setCurrentUser(me); // null if unauthenticated
      } catch (e) {
        console.error("Failed to fetch current user for match gating:", e);
        setCurrentUser(null);
      }
    })();
  }, []);

  // Redirect guard: if patient already has preferences, send them to the preference tab
  useEffect(() => {
    if (!currentUser) return;
    const pid = currentUser.patientId || patientId;
    if (currentUser.userType !== "PATIENT" || !pid) return;

    let cancelled = false;
    (async () => {
      try {
        const prefs = await getPatientPreference(pid);
        if (!cancelled && prefs) {
          navigate("/PatientProfile?tab=patient preference", { replace: true });
        }
      } catch {
        // API error → form remains usable as fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, patientId, navigate]);

  useEffect(() => {
    if (!formData.stateId) {
      setCityOptions([]);
      return;
    }
    let on = true;
    (async () => {
      setLoadingCities(true);
      try {
        const cities = await getCitiesByState(Number(formData.stateId));
        if (on)
          setCityOptions(cities.map((c) => ({ value: c.id, label: c.name })));
      } finally {
        if (on) setLoadingCities(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [formData.stateId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // State selection: store numeric stateId, clear cityId when state changes
    if (name === "stateId") {
      const selected = stateOptions.find(
        (s) => String(s.value) === String(value),
      );
      const stateName = selected?.label ?? "";
      const stateCode = usStates.find((s) => s.name === stateName)?.code ?? "";

      setFormData((prev) => ({
        ...prev,
        stateId: value ? Number(value) : "",
        state: stateCode, // ✅ store GA, TX, MA
        cityId: "",
        city: "",
      }));
      return;
    }

    if (name === "cityId") {
      const cityName =
        cityOptions.find((c) => String(c.value) === String(value))?.label || "";

      setFormData((prev) => ({
        ...prev,
        cityId: value ? Number(value) : "",
        city: cityName,
      }));
      return;
    }

    // Insurance: store as array of plan names
    if (name === "insuranceIds") {
      setFormData((prev) => ({ ...prev, insuranceIds: value ? [value] : [] }));
      return;
    }

    // Language: store as array of numeric ids
    if (name === "languageNames") {
      // store a single selected language as a one-element array of strings
      setFormData((prev) => ({
        ...prev,
        languageNames: value ? [String(value)] : [],
      }));
      return;
    }

    // Age min/max numeric parsing
    if (name === "minYearsOfExperience" || name === "maxYearsOfExperience") {
      const parsed = value === "" ? "" : Number(value);
      setFormData((prev) => ({ ...prev, [name]: parsed }));
      return;
    }

    // Default: simple string fields
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Core preferences submission logic (extracted from handleSubmit)
  const handleSubmitPreferencesCore = async () => {
    // Validation
    if (
      formData.minYearsOfExperience !== "" &&
      formData.maxYearsOfExperience !== "" &&
      formData.minYearsOfExperience !== null &&
      formData.maxYearsOfExperience !== null &&
      Number(formData.maxYearsOfExperience) <
        Number(formData.minYearsOfExperience)
    ) {
      alert(
        "Max years of experience cannot be less than Min years of experience.",
      );
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      const payload = formatPatientPreferencesSavePayload(formData);
      // ✅ Ensure patientId is in the payload
      payload.patientId = currentUser?.patientId || patientId;
      await savePatientPreferences(payload);
      setSuccess(true);

      // Navigate to Provider Matches tab
      if (switchTab) {
        switchTab("provider matches");
      } else {
        navigate("/PatientProfile?tab=provider matches");
      }
    } catch (err) {
      console.error("Error saving preferences:", err);
      alert("Error saving preferences. Check console.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // GATING: Guest / not logged in
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    // GATING: BASIC_ACCOUNT -> show complete profile modal
    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }

    // COMPLETED_PROFILE → proceed immediately
    await handleSubmitPreferencesCore();
  };

  /*const handleSubmit = async (e) => {
    e.preventDefault();

    // GATING:
    // Guest / not logged in
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    // BASIC_ACCOUNT -> show complete profile modal
    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }

    // Basic validation: ageMax must be >= ageMin
    if (
      formData.minYearsOfExperience !== "" &&
      formData.maxYearsOfExperience !== "" &&
      formData.minYearsOfExperience !== null &&
      formData.maxYearsOfExperience !== null &&
      Number(formData.maxYearsOfExperience) <
        Number(formData.minYearsOfExperience)
    ) {
      alert(
        "Max years of experience cannot be less than Min years of experience.",
      );
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      // Format payload via your shared formatter to ensure arrays/numbers/enums are correct
      const payload = formatPatientPreferencesSavePayload(formData);
      await savePatientPreferences(payload);
      setSuccess(true);
      // ✅ Use switchTab to go to Provider Matches tab
      if (switchTab) {
        switchTab("provider matches");
      } else {
        // Navigate to PatientProfile with the provider matches tab active
        navigate("/PatientProfile?tab=provider matches");
      }
    } catch (err) {
      console.error("Error saving preferences:", err);
      alert("Error saving preferences. Check console.");
    } finally {
      setLoading(false);
    }
  };*/

  /**
   * 🔥 Flexible dropdown renderer (keeps original structure)
   * - If formData[name] is an array, show its first element in the select
   */
  const renderSelect = (label, name, options, disabled = false) => {
    const current = formData[name];
    const selected = Array.isArray(current)
      ? (current[0] ?? "") // first element if array
      : (current ?? ""); // empty string if undefined/null

    return (
      <div className="grid grid-cols-[11rem_1fr] items-center gap-4" key={name}>
        <label htmlFor={name} className="text-sm font-semibold text-gray-700">
          {label}
        </label>
        <select
          id={name}
          name={name}
          value={selected}
          onChange={handleChange}
          disabled={disabled}
          className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 ${
            disabled ? "bg-gray-50 text-gray-400" : ""
          }`}
        >
          <option value="">Select {label.toLowerCase()}</option>
          {options.map((opt) => {
            // if option is string, use it for both value and label
            if (typeof opt === "string") {
              return (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              );
            }
            // otherwise fall back to { value, label } shape if provided
            const v = opt.value ?? opt.id;
            const lbl = opt.label ?? opt.name ?? opt.planName ?? String(v);
            return (
              <option key={String(v)} value={v}>
                {lbl}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  //const cities = formData.state ? stateToCities[formData.state] || [] : [];
  // ✅ Disable submit until required fields are selected
  const isFormValid =
    formData.specialty &&
    formData.stateId &&
    formData.cityId &&
    formData.insuranceIds.length > 0 &&
    // ✅ If both optional experience fields are filled, max must be >= min
    (formData.minYearsOfExperience === "" ||
      formData.maxYearsOfExperience === "" ||
      Number(formData.maxYearsOfExperience) >=
        Number(formData.minYearsOfExperience));

  return (
    <>
      <CompletePatientProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        token={token}
        currentUser={currentUser}
        onCompleted={async () => {
          // Mark user as COMPLETED_PROFILE locally
          setCurrentUser((prev) =>
            prev ? { ...prev, accountPhase: "COMPLETED_PROFILE" } : prev,
          );
          setShowCompleteProfileModal(false);

          // ✅ Wait a moment for backend to process
          await new Promise((resolve) => setTimeout(resolve, 500));

          // ✅ Refresh Keycloak token to get updated claims
          try {
            await keycloak.updateToken(30);
          } catch (err) {
            console.error("Failed to refresh token:", err);
          }

          // ✅ Automatically submit preferences with the form data
          await handleSubmitPreferencesCore();
        }}
      />

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-6 relative">
            <button
              type="button"
              onClick={closeAuthModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-4 text-center">
              Welcome to MedMaze
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
              Sign in or sign up to save preferences and get provider matches.
            </p>
            <button
              type="button"
              onClick={handleSignIn}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg mb-3 transition-colors"
            >
              Sign In
            </button>
            <div className="flex items-center my-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="mx-2 text-gray-400 text-xs">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <button
              type="button"
              onClick={handleSignUp}
              className="w-full border border-teal-600 text-teal-600 hover:bg-teal-50 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50">
        <Header />

        <div className="max-w-2xl mx-auto px-6 py-8">
          <button
            onClick={() => window.history.back()}
            className="mb-6 text-sm font-medium text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors"
          >
            ← Back
          </button>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-teal-500 pl-3 mb-8">
              Provider Preference Form
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {renderSelect(
                "Specialty",
                "specialty",
                (specialties || []).map((s) => ({
                  value: s.name,
                  label: s.name,
                })),
              )}

              {renderSelect(
                loadingStates
                  ? "Preferred State (loading…)"
                  : "Preferred State",
                "stateId",
                stateOptions,
              )}

              {renderSelect(
                loadingCities ? "Preferred City (loading…)" : "Preferred City",
                "cityId",
                cityOptions,
                !formData.stateId || loadingCities,
              )}

              {renderSelect(
                "Insurance Accepted",
                "insuranceIds",
                (insurances || []).map((i) => ({
                  value: i.planName,
                  label: i.planName,
                })),
              )}

              {renderSelect(
                "Preferred Language",
                "languageNames",
                (LANGUAGES || []).map((l) =>
                  typeof l === "string" ? l : (l.label ?? l.name),
                ),
              )}

              {renderSelect("Ethnicity", "ethnicity", ETHNICITIES)}

              {/* Min Years of Experience */}
              <div className="grid grid-cols-[11rem_1fr] items-center gap-4">
                <label
                  htmlFor="minYearsOfExperience"
                  className="text-sm font-semibold text-gray-700"
                >
                  Min Experience (yrs)
                </label>
                <input
                  type="number"
                  id="minYearsOfExperience"
                  name="minYearsOfExperience"
                  min="0"
                  value={formData.minYearsOfExperience}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                />
              </div>

              {/* Max Years of Experience */}
              <div className="grid grid-cols-[11rem_1fr] items-start gap-4">
                <label
                  htmlFor="maxYearsOfExperience"
                  className="text-sm font-semibold text-gray-700 pt-2"
                >
                  Max Experience (yrs)
                </label>
                <div>
                  <input
                    type="number"
                    id="maxYearsOfExperience"
                    name="maxYearsOfExperience"
                    min="0"
                    value={formData.maxYearsOfExperience}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  />
                  {formData.minYearsOfExperience !== "" &&
                    formData.maxYearsOfExperience !== "" &&
                    Number(formData.maxYearsOfExperience) <
                      Number(formData.minYearsOfExperience) && (
                      <p className="text-red-600 text-sm mt-1">
                        Max must be ≥ Min ({formData.minYearsOfExperience})
                      </p>
                    )}
                </div>
              </div>

              {renderSelect("Gender", "gender", GENDER)}

              {/* Submit */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className={`w-full py-2.5 rounded-full font-semibold text-sm transition-colors ${
                    loading || !isFormValid
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-teal-600 hover:bg-teal-700 text-white"
                  }`}
                >
                  {loading ? "Saving..." : "Submit Preferences"}
                </button>
                {!isFormValid && (
                  <p className="text-red-600 text-sm mt-2 text-center">
                    Complete specialty, state, city, and insurance before
                    submitting preferences.
                  </p>
                )}
                {success && (
                  <p className="text-teal-600 text-sm mt-2 text-center">
                    Preferences saved successfully!
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
